import React from "react";
import classNames from "classnames";
import { noop } from "@common/helpers/function";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { Align, Side } from "@design-system/overlay/overlayPositioning";
import { SpinnerIcon } from "@icons";
import { Button } from "./Button";
import type { ButtonProps } from "./Button";
import { WithHotkey } from "./WithHotkey";
import type { WithHotkeyProps } from "./WithHotkey";
import styles from "./ButtonWithLoader.module.scss";

type DistributiveOmit<T, K extends keyof T> = T extends T ? Omit<T, K> : never;

// The loader renders its own label and spinner children, so `asChild` — which
// hands rendering to a single child element — is not one of its shapes.
type LoaderButtonProps = Exclude<ButtonProps, { asChild: true }>;

// An inline hotkey renders a hint beside the label, which an icon-only button
// has no label to sit beside — the same rule Button's union applies to slots.
type LabelledLoaderButtonProps = Exclude<LoaderButtonProps, { iconOnly: true }>;

interface LoaderOwnProps {
    loading: boolean;
    setLoading?: (isLoading: boolean) => void;
    onClick?: (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => Promise<void> | void;
    tooltip?: React.ReactNode;
    tooltipSide?: Side;
    tooltipAlign?: Align;
}

type BaseButtonWithLoaderProps = DistributiveOmit<
    LoaderButtonProps,
    "onClick"
> &
    LoaderOwnProps;

type LabelledButtonWithLoaderProps = DistributiveOmit<
    LabelledLoaderButtonProps,
    "onClick"
> &
    LoaderOwnProps;

interface HotkeyProps {
    hotKey: WithHotkeyProps["hotKey"];
    hotKeyOptions: WithHotkeyProps["hotKeyOptions"];
}

/**
 * The hotkey props are both-or-neither, and `"inline"` is reserved for buttons
 * that have a label to put the hint next to.
 */
export type ButtonWithLoaderProps =
    | (LabelledButtonWithLoaderProps &
          HotkeyProps & { hotKeyDisplayPosition: "inline" })
    | (BaseButtonWithLoaderProps &
          HotkeyProps & { hotKeyDisplayPosition?: "none" | "tooltip" })
    | (BaseButtonWithLoaderProps & {
          hotKey?: never;
          hotKeyOptions?: never;
          hotKeyDisplayPosition?: never;
      });

/**
 * Button that swaps its label for a spinner while `loading`. When `onClick`
 * returns a promise the loading state is driven for the caller through
 * `setLoading`; clicks are ignored until it settles.
 */
export const ButtonWithLoader = ({
    loading,
    setLoading = noop,
    onClick,
    children,
    className,
    hotKey,
    hotKeyOptions,
    hotKeyDisplayPosition,
    tooltip,
    tooltipSide,
    tooltipAlign,
    ...buttonProps
}: ButtonWithLoaderProps) => {
    // The controlled `loading` prop only flips after a parent re-render, so a
    // second click in the same tick would pass a prop-based guard; the ref
    // closes that window.
    const isInFlightRef = React.useRef(false);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!onClick || loading || isInFlightRef.current) {
            return;
        }
        setLoading(true);
        const result = onClick(event);
        if (result) {
            isInFlightRef.current = true;
            void result.finally(() => {
                isInFlightRef.current = false;
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    };
    // The hint sits inside the label span so it is hidden with the label while
    // loading, rather than floating beside the spinner.
    const renderButton = (hotkeyHint?: React.ReactNode) => (
        <Button
            {...buttonProps}
            className={classNames(styles.buttonWithLoader, className, {
                [styles.loading]: loading,
            })}
            onClick={handleClick}
        >
            <span className={styles.content}>
                {children}
                {hotkeyHint}
            </span>
            <span className={styles.spinner}>
                <SpinnerIcon className="icon-color-current" />
            </span>
        </Button>
    );

    if (hotKey && hotKeyOptions) {
        const hotkeyProps = {
            hotKey,
            hotKeyOptions,
            tooltip,
            tooltipPlacement: tooltipSide,
            tooltipAlign,
        };
        return hotKeyDisplayPosition === "inline" ? (
            <WithHotkey {...hotkeyProps} hotKeyDisplayPosition="inline">
                {renderButton}
            </WithHotkey>
        ) : (
            <WithHotkey
                {...hotkeyProps}
                hotKeyDisplayPosition={hotKeyDisplayPosition}
            >
                {renderButton()}
            </WithHotkey>
        );
    }
    return (
        <Tooltip content={tooltip} side={tooltipSide} align={tooltipAlign}>
            {renderButton()}
        </Tooltip>
    );
};
